import React from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "../../lib/maplibre-worker";
import { Link } from "@tanstack/react-router";
import type { AnalysedPoint, RouteAnalysis } from "@atlas/shared";
import { isCrossedSection } from "../../api/route-analyser";
import { landmarkCategoryLabel } from "../../lib/landmark-icons";
import { MAP_COLORS } from "./map-colors";
import { createMarkerImage, LANDMARK_GLYPHS, PEAK_GLYPH, LANDMARK_MARKER_PX, PEAK_MARKER_PX } from "./AtlasMap";

// Route analyser map (spec 27): the planned route, what it would collect,
// near misses with a dashed spur and distance label, and the uncovered gaps
// on trail sections it falls short of. Same marker glyphs as the explore map.
//
// Colours: collect = success (spruce), already on your record = muted grey,
// near miss = sky. Not Summit Gold anywhere: gold means achievement, and on
// this page it belongs only to the Pro "Would unlock" list. (--status-warning
// resolves to gold in every theme, so near misses use the in-progress sky.)
//
// Whole trails (RouteAnalysis.trails): every section of a trail the route
// completes a section of, drawn under the route. Sections the route completes
// are success green, sections it walks part of are sky, sections already on
// the record are solid grey and the rest of the trail is dashed grey. Paths
// the route only crosses (under 40%, spec 27 V9) count as the rest. Official
// alternates (spec 33, e.g. the NDW Canterbury loop) are drawn by the same rule.
//
// Nearby items (beyond the near-miss band, within a few km) are map context
// only: small grey pins drawn under everything else, no spur and no detour
// estimate. They show where a bigger deviation would collect more; Atlas
// never reroutes. Ones already on the record are fainter still.

const MAP_STYLE = "https://tiles.openfreemap.org/styles/dark";
const { spruce: COLLECT, grey: ON_RECORD, sky: NEAR, textOnGrey: ROUTE } = MAP_COLORS;

type PinState = "collect" | "record" | "near";
const PIN_COLORS: Record<PinState, string> = { collect: COLLECT, record: ON_RECORD, near: NEAR };

export interface RouteMapFocus {
  /** Changes on every request so re-clicking the same item flies again. */
  nonce: number;
  coordinates: [number, number][];
}

export interface RouteAnalysisMapProps {
  analysis: RouteAnalysis;
  focus?: RouteMapFocus | null;
  /** Nearby pins (map context only). On by default. */
  showNearby?: boolean;
  style?: React.CSSProperties;
}

type Picked =
  | { type: "pin"; name: string; label: string }
  | {
      type: "nearby";
      kind: "peak" | "landmark";
      slug: string;
      name: string;
      meta: string;
      distance: string;
      onRecord: boolean;
    };

type FC = { type: "FeatureCollection"; features: unknown[] };

function formatM(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

function pinFeature(p: AnalysedPoint, kind: "peak" | "landmark", state: PinState, label: string) {
  const category = p.category && p.category in LANDMARK_GLYPHS ? p.category : "trig_point";
  return {
    type: "Feature" as const,
    geometry: { type: "Point" as const, coordinates: [p.lng, p.lat] },
    properties: {
      kind,
      state,
      name: p.name,
      label,
      icon: kind === "peak" ? `ra-peak-${state}` : `ra-lm-${category}-${state}`,
    },
  };
}

function nearbyFeature(
  p: AnalysedPoint,
  kind: "peak" | "landmark",
  distanceM: number,
  onRecord: boolean | undefined,
) {
  const category = p.category && p.category in LANDMARK_GLYPHS ? p.category : "trig_point";
  const meta =
    kind === "peak"
      ? p.elevationM != null
        ? `Peak · ${Math.round(p.elevationM).toLocaleString("en-GB")} m`
        : "Peak"
      : landmarkCategoryLabel(p.category);
  return {
    type: "Feature" as const,
    geometry: { type: "Point" as const, coordinates: [p.lng, p.lat] },
    properties: {
      kind,
      slug: p.slug,
      name: p.name,
      meta,
      distance: formatM(distanceM),
      rec: onRecord === true,
      // Nearest first when pins collide; anything on the record yields.
      sort: distanceM + (onRecord === true ? 100_000 : 0),
      icon: kind === "peak" ? "ra-peak-nearby" : `ra-lm-${category}-nearby`,
    },
  };
}

export type TrailSectionState = "collected" | "partial" | "record" | "rest";

/** Shared with the legend and the trail panel so the colours mean one thing. */
/**
 * Sections and official alternates (spec 33) share the rule, except where
 * "partly walked" starts: a section under 40% is a path the route only
 * crosses (spec 27 V9), but an alternate is 40-50 km long, so 39% of one is
 * ~20 km walked along it. Alternates count as partly walked from 10%.
 */
export const ALTERNATE_PARTIAL_FRACTION = 0.1;

export function trailSectionState(s: {
  touched: boolean;
  completes: boolean;
  fractionAfter: number;
  completedBefore?: boolean;
}, alternate = false): TrailSectionState {
  if (s.completedBefore === true) return "record";
  // Not "touched": a main-line section credited by walking its official
  // alternate (spec 33) is completed by this walk without the route on it.
  if (s.completes) return "collected";
  if (s.touched && (alternate ? s.fractionAfter >= ALTERNATE_PARTIAL_FRACTION : !isCrossedSection(s))) {
    return "partial";
  }
  return "rest";
}

function buildSources(a: RouteAnalysis): {
  route: FC;
  pins: FC;
  spurs: FC;
  gaps: FC;
  ends: FC;
  nearby: FC;
  trails: FC;
} {
  const coords = a.route.geojson.coordinates;
  // Personal-only flags: absent means unknown, which draws as "would collect".
  const collectState = (p: AnalysedPoint): PinState => (p.isNew === false ? "record" : "collect");
  const nearState = (onRecord: boolean | undefined): PinState => (onRecord === true ? "record" : "near");

  const collectLabel = (p: AnalysedPoint) =>
    p.isNew === true ? "Would collect, new to you" : p.isNew === false ? "Already on your record" : "Would collect";
  const nearLabel = (distanceM: number, onRecord: boolean | undefined) =>
    `Missed by ${formatM(distanceM)}${onRecord === true ? ", already on your record" : ""}`;

  const pins = [
    ...a.nearMisses.peaks.map((n) => pinFeature(n.item, "peak", nearState(n.onRecord), nearLabel(n.distanceM, n.onRecord))),
    ...a.nearMisses.landmarks.map((n) =>
      pinFeature(n.item, "landmark", nearState(n.onRecord), nearLabel(n.distanceM, n.onRecord)),
    ),
    ...a.collects.landmarks.map((p) => pinFeature(p, "landmark", collectState(p), collectLabel(p))),
    ...a.collects.peaks.map((p) => pinFeature(p, "peak", collectState(p), collectLabel(p))),
  ];

  const spurs = [...a.nearMisses.peaks, ...a.nearMisses.landmarks].map((n) => ({
    type: "Feature" as const,
    geometry: n.spur,
    properties: { label: formatM(n.distanceM), state: nearState(n.onRecord) },
  }));

  // Paths the route only crosses (spec 27 V9) draw no gap: their "gap" is
  // most of the section, which would read as a near miss.
  const gaps = a.nearMisses.trailSections.filter((s) => !isCrossedSection(s)).map((s) => ({
    type: "Feature" as const,
    geometry: s.gaps,
    properties: { name: s.sectionName },
  }));

  const nearby = [
    ...a.nearby.peaks.map((n) => nearbyFeature(n.item, "peak", n.distanceM, n.onRecord)),
    ...a.nearby.landmarks.map((n) => nearbyFeature(n.item, "landmark", n.distanceM, n.onRecord)),
  ];

  const trails = (a.trails ?? []).flatMap((t) => [
    ...t.sections.map((sec) => ({ sec, alternate: false })),
    ...(t.alternates ?? []).map((sec) => ({ sec, alternate: true })),
  ].map(({ sec, alternate }) => ({
      type: "Feature" as const,
      geometry: sec.geojson,
      properties: { state: trailSectionState(sec, alternate), name: sec.name, trail: t.name },
    })),
  );

  return {
    trails: { type: "FeatureCollection", features: trails },
    route: { type: "FeatureCollection", features: [{ type: "Feature", geometry: a.route.geojson, properties: {} }] },
    nearby: { type: "FeatureCollection", features: nearby },
    pins: { type: "FeatureCollection", features: pins },
    spurs: { type: "FeatureCollection", features: spurs },
    gaps: { type: "FeatureCollection", features: gaps },
    ends: {
      type: "FeatureCollection",
      features:
        coords.length >= 2
          ? [
              { type: "Feature", geometry: { type: "Point", coordinates: coords[0] }, properties: { end: "start" } },
              {
                type: "Feature",
                geometry: { type: "Point", coordinates: coords[coords.length - 1] },
                properties: { end: "finish" },
              },
            ]
          : [],
    },
  };
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

function fitRoute(map: maplibregl.Map, a: RouteAnalysis, animate: boolean) {
  const [w, s, e, n] = a.route.bbox;
  if (![w, s, e, n].every(Number.isFinite)) return;
  map.fitBounds(
    [
      [w, s],
      [e, n],
    ],
    { padding: 48, duration: animate && !prefersReducedMotion() ? 600 : 0, maxZoom: 15 },
  );
}

const SOURCE_IDS = ["trails", "route", "pins", "spurs", "gaps", "ends", "nearby"] as const;
const CLICKABLE = ["pins-symbol", "nearby-symbol"];

export function RouteAnalysisMap({ analysis, focus, showNearby = true, style }: RouteAnalysisMapProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<maplibregl.Map | null>(null);
  const loadedRef = React.useRef(false);
  const dataRef = React.useRef(analysis);
  dataRef.current = analysis;
  const showNearbyRef = React.useRef(showNearby);
  showNearbyRef.current = showNearby;
  const [picked, setPicked] = React.useState<Picked | null>(null);

  React.useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: [-3.1, 54.5],
      zoom: 9,
      attributionControl: { compact: true },
      fadeDuration: 0,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    // The frame can change size after mount (a shared link renders straight
    // into the results layout while the narrow/wide breakpoint settles), and
    // MapLibre only measures its container on window resize.
    const resizer = new ResizeObserver(() => map.resize());
    resizer.observe(containerRef.current);

    map.on("load", () => {
      for (const layer of map.getStyle().layers ?? []) {
        if ("source-layer" in layer && layer["source-layer"] === "aeroway") {
          map.setLayoutProperty(layer.id, "visibility", "none");
        }
      }

      // The explore map's own glyphs, tinted by state.
      for (const [state, color] of Object.entries(PIN_COLORS)) {
        map.addImage(`ra-peak-${state}`, createMarkerImage(color, PEAK_MARKER_PX, PEAK_GLYPH), { pixelRatio: 2 });
        for (const [category, glyph] of Object.entries(LANDMARK_GLYPHS)) {
          map.addImage(`ra-lm-${category}-${state}`, createMarkerImage(color, LANDMARK_MARKER_PX, glyph), {
            pixelRatio: 2,
          });
        }
      }
      // Nearby: the same glyphs on the explore map's "not collected" grey.
      map.addImage("ra-peak-nearby", createMarkerImage(ON_RECORD, PEAK_MARKER_PX, PEAK_GLYPH), { pixelRatio: 2 });
      for (const [category, glyph] of Object.entries(LANDMARK_GLYPHS)) {
        map.addImage(`ra-lm-${category}-nearby`, createMarkerImage(ON_RECORD, LANDMARK_MARKER_PX, glyph), {
          pixelRatio: 2,
        });
      }

      const sources = buildSources(dataRef.current);
      for (const id of SOURCE_IDS) map.addSource(id, { type: "geojson", data: sources[id] as never });

      // The rest of the trail sits under the route's halo; the route's own
      // sections sit between the halo and the route, so they read as a
      // coloured casing along the line and full width away from it.
      map.addLayer({
        id: "trails-rest",
        type: "line",
        source: "trails",
        filter: ["==", ["get", "state"], "rest"],
        layout: { "line-cap": "butt", "line-join": "round" },
        paint: { "line-color": ON_RECORD, "line-width": 2.5, "line-opacity": 0.95, "line-dasharray": [2, 1.5] },
      });
      map.addLayer({
        id: "trails-record",
        type: "line",
        source: "trails",
        filter: ["==", ["get", "state"], "record"],
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": ON_RECORD, "line-width": 3.5, "line-opacity": 0.95 },
      });
      map.addLayer({
        id: "route-halo",
        type: "line",
        source: "route",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "rgba(9,12,16,0.6)", "line-width": 9 },
      });
      map.addLayer({
        id: "trails-walked",
        type: "line",
        source: "trails",
        filter: ["in", ["get", "state"], ["literal", ["collected", "partial"]]],
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": ["match", ["get", "state"], "collected", COLLECT, NEAR],
          "line-width": 7,
          "line-opacity": 0.95,
        },
      });
      map.addLayer({
        id: "route-line",
        type: "line",
        source: "route",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": ROUTE, "line-width": 3.5, "line-opacity": 0.95 },
      });
      map.addLayer({
        id: "gaps-line",
        type: "line",
        source: "gaps",
        layout: { "line-cap": "butt", "line-join": "round" },
        paint: { "line-color": NEAR, "line-width": 4, "line-dasharray": [1.5, 1.2], "line-opacity": 0.95 },
      });
      // Under the spurs and every collected or near-miss pin. Not allowed to
      // overlap, so dense areas declutter (nearest kept) until you zoom in.
      map.addLayer({
        id: "nearby-symbol",
        type: "symbol",
        source: "nearby",
        layout: {
          visibility: showNearbyRef.current ? "visible" : "none",
          "icon-image": ["get", "icon"],
          "icon-size": ["case", ["get", "rec"], 0.55, 0.68],
          "icon-allow-overlap": false,
          "icon-padding": 1,
          "icon-anchor": "center",
          "symbol-sort-key": ["get", "sort"],
        },
        paint: { "icon-opacity": ["case", ["get", "rec"], 0.4, 0.78] },
      });
      map.addLayer({
        id: "spurs-line",
        type: "line",
        source: "spurs",
        layout: { "line-cap": "round" },
        paint: {
          "line-color": ["match", ["get", "state"], "record", ON_RECORD, NEAR],
          "line-width": 2.5,
          "line-dasharray": [1.2, 1.4],
        },
      });
      map.addLayer({
        id: "spurs-label",
        type: "symbol",
        source: "spurs",
        layout: {
          "symbol-placement": "line-center",
          "text-field": ["get", "label"],
          // Same font stack as the explore map: an unknown font draws nothing.
          "text-font": ["Noto Sans Bold"],
          "text-size": 11,
          "text-offset": [0, -0.9],
          "text-allow-overlap": false,
        },
        paint: { "text-color": ROUTE, "text-halo-color": "rgba(9,12,16,0.85)", "text-halo-width": 1.4 },
      });
      map.addLayer({
        id: "ends-circle",
        type: "circle",
        source: "ends",
        paint: {
          "circle-color": ["match", ["get", "end"], "finish", "#FFFFFF", "#090C10"],
          "circle-radius": 5,
          "circle-stroke-width": 2,
          "circle-stroke-color": ["match", ["get", "end"], "finish", "#090C10", "#FFFFFF"],
        },
      });
      map.addLayer({
        id: "pins-symbol",
        type: "symbol",
        source: "pins",
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": ["match", ["get", "state"], "near", 0.85, 1],
          "icon-allow-overlap": true,
          "icon-anchor": "center",
          "symbol-sort-key": ["match", ["get", "state"], "collect", 2, "near", 1, 0],
        },
        paint: { "icon-opacity": ["match", ["get", "state"], "collect", 1, 0.8] },
      });

      // One handler for both pin layers: the first hit is the topmost, so a
      // collected or near-miss pin wins over a nearby one underneath it.
      map.on("click", (e) => {
        const layers = CLICKABLE.filter((id) => map.getLayer(id));
        const hit = map.queryRenderedFeatures(e.point, { layers })[0];
        if (!hit) {
          setPicked(null);
          return;
        }
        const props = hit.properties as Record<string, unknown>;
        if (hit.layer.id === "nearby-symbol") {
          setPicked({
            type: "nearby",
            kind: props["kind"] === "peak" ? "peak" : "landmark",
            slug: String(props["slug"]),
            name: String(props["name"]),
            meta: String(props["meta"]),
            distance: String(props["distance"]),
            onRecord: props["rec"] === true,
          });
        } else if (typeof props["name"] === "string") {
          setPicked({ type: "pin", name: props["name"], label: String(props["label"] ?? "") });
        }
      });
      for (const id of CLICKABLE) {
        map.on("mouseenter", id, () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", id, () => {
          map.getCanvas().style.cursor = "";
        });
      }

      loadedRef.current = true;
      fitRoute(map, dataRef.current, false);

      // MapLibre opens compact attribution expanded, which covers a corner
      // of a phone-width map. Start it collapsed; the (i) button still opens it.
      map.once("idle", () => {
        map
          .getContainer()
          .querySelector(".maplibregl-ctrl-attrib.maplibregl-compact-show")
          ?.classList.remove("maplibregl-compact-show");
      });
    });

    return () => {
      resizer.disconnect();
      loadedRef.current = false;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // New result (another version of the route, or the personal re-run).
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    const sources = buildSources(analysis);
    for (const id of SOURCE_IDS) {
      (map.getSource(id) as maplibregl.GeoJSONSource | undefined)?.setData(sources[id] as never);
    }
    fitRoute(map, analysis, true);
    setPicked(null);
  }, [analysis]);

  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    map.setLayoutProperty("nearby-symbol", "visibility", showNearby ? "visible" : "none");
    if (!showNearby) setPicked((p) => (p?.type === "nearby" ? null : p));
  }, [showNearby]);

  // Fly to a near miss's spur (or a trail gap) when the results list asks.
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus || focus.coordinates.length === 0) return;
    let w = Infinity,
      s = Infinity,
      e = -Infinity,
      n = -Infinity;
    for (const [lng, lat] of focus.coordinates) {
      w = Math.min(w, lng);
      e = Math.max(e, lng);
      s = Math.min(s, lat);
      n = Math.max(n, lat);
    }
    map.fitBounds(
      [
        [w, s],
        [e, n],
      ],
      { padding: 90, maxZoom: 16, duration: prefersReducedMotion() ? 0 : 900 },
    );
  }, [focus]);

  return (
    <div style={{ position: "absolute", inset: 0, ...style }}>
      <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />
      {picked && (
        <div
          className="atlas-glass"
          role="status"
          style={{
            position: "absolute",
            left: 12,
            bottom: 12,
            maxWidth: "calc(100% - 24px)",
            padding: "8px 12px",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-default)",
            boxShadow: "var(--shadow-sm)",
            // Only the nearby card has a link in it.
            pointerEvents: picked.type === "nearby" ? "auto" : "none",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{picked.name}</div>
          {picked.type === "pin" ? (
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{picked.label}</div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                {picked.meta} · <span className="tnum">{picked.distance}</span> from the route
                {picked.onRecord ? " · already on your record" : ""}
              </div>
              <Link
                {...(picked.kind === "peak"
                  ? { to: "/peaks/$slug" as const, params: { slug: picked.slug } }
                  : { to: "/landmarks/$slug" as const, params: { slug: picked.slug } })}
                style={{
                  display: "inline-block",
                  marginTop: 4,
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--text-secondary)",
                }}
              >
                {picked.kind === "peak" ? "View peak" : "View landmark"}
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}
